import {debounce} from 'lodash';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useParams} from 'react-router-dom';

import {mediaFullscreenModalOpened} from '../../../services/analytics/productEvents';
import {useViewer} from '../../../state/viewer/hooks';
import {useRampFlagMediaBrowserModalEnabled} from '../../../util/rampFeatureFlags';
import {keyDisplayName} from '../../../util/runs';
import {RunWithHistoryAndMediaWandb, TileMediaSpec} from '../../MediaCard';
import {useStablePanelId} from '../../Panel/PanelIDContext';
import {StepMeta} from '../types';
import {ModalData} from './types';

type ModalContextValue = {
  // State management
  isOpen: boolean;
  modalData: ModalData | null;

  // Actions
  openModal: (data: ModalData) => void;
  updateModalData: (data: ModalData) => void;
  closeModal: () => void;

  // Navigation actions
  selectRun: (run: RunWithHistoryAndMediaWandb) => void;
  navigateToPrevious: () => void;
  navigateToNext: () => void;

  // Step/Index actions
  updateStep: (step: number) => void;
  updateIndex: (index: number) => void;
  debouncedUpdateStep: (step: number) => void;
  debouncedUpdateIndex: (index: number) => void;

  // Processed data for components
  currentTileMediaSpec: TileMediaSpec | null;
  navigation: {
    hasNext: boolean;
    hasPrevious: boolean;
    currentRun: RunWithHistoryAndMediaWandb | null;
    availableRuns: RunWithHistoryAndMediaWandb[];
  };
  sliderData: {
    stepSlider: {
      min: number;
      max: number;
      current: number;
      steps: number[];
      key: string;
      label: string;
      keyIsStep: boolean;
      allowAnyValue: boolean;
      sliderStepMap?: Map<number, number> | null;
    } | null;
    indexSlider: {
      min: number;
      max: number;
      current: number;
      indices: number[];
    } | null;
  };
  sliderConfig: {
    snapToExistingStep: boolean;
    stepStrideLength: number;
  };
};

const ModalContext = createContext<ModalContextValue | null>(null);

export const useMediaBrowserModalContext = (): ModalContextValue => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error(
      'useMediaBrowserModalContext must be used within a MediaBrowserModalProvider'
    );
  }
  return context;
};

type MediaBrowserModalProviderProps = {
  children: React.ReactNode;
  runsWithMedia: RunWithHistoryAndMediaWandb[];
  sliderConfig: {
    snapToExistingStep: boolean;
    stepStrideLength: number;
  };
  stepMeta?: StepMeta;
  onModalClose?: (finalStep: number, finalIndex: number) => void;
};

export const MediaBrowserModalProvider: React.FC<
  MediaBrowserModalProviderProps
> = ({
  children,
  runsWithMedia,
  sliderConfig,
  stepMeta: initialStepMeta,
  onModalClose,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalData, setModalData] = useState<ModalData | null>(null);
  const [selectedRun, setSelectedRun] =
    useState<RunWithHistoryAndMediaWandb | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const stepMeta = initialStepMeta;
  const {entityName} = useParams<{entityName: string}>();

  // hack while we have 2 versions of the modal, will remove when fully ramped
  const shouldUseFullscreenModal =
    useRampFlagMediaBrowserModalEnabled(entityName);
  // tracking parameters
  const user = useViewer()?.username;
  const panelId = useStablePanelId();

  const debouncedUpdateStep = useMemo(
    () =>
      debounce((step: number) => {
        setCurrentStep(step);
      }, 16),
    []
  );

  const debouncedUpdateIndex = useMemo(
    () =>
      debounce((index: number) => {
        setCurrentIndex(index);
      }, 16),
    []
  );

  useEffect(() => {
    return () => {
      debouncedUpdateStep.cancel();
      debouncedUpdateIndex.cancel();
    };
  }, [debouncedUpdateStep, debouncedUpdateIndex]);

  const openModal = useCallback(
    (data: ModalData) => {
      debouncedUpdateStep.flush();
      debouncedUpdateIndex.flush();

      setModalData(data);
      const initialRun = runsWithMedia.find(
        run => run.name === data.runSignature.runName
      );
      setSelectedRun(initialRun ?? null);
      setCurrentStep(data.tileMediaSpec.mediaStep);
      setCurrentIndex(data.tileMediaSpec.mediaIndex);

      setIsOpen(true);
      if (shouldUseFullscreenModal) {
        mediaFullscreenModalOpened({
          metricKey: data.tileMediaSpec.mediaKey,
          entityName: data.runSignature.entityName,
          projectName: data.runSignature.projectName,
          user: user,
          panelId: panelId,
        });
      }
    },
    [
      runsWithMedia,
      debouncedUpdateStep,
      debouncedUpdateIndex,
      user,
      panelId,
      shouldUseFullscreenModal,
    ]
  );

  const closeModal = useCallback(() => {
    if (onModalClose) {
      onModalClose(currentStep, currentIndex);
    }

    debouncedUpdateStep.flush();
    debouncedUpdateIndex.flush();

    setIsOpen(false);
    setModalData(null);
    setSelectedRun(null);
    setCurrentStep(0);
    setCurrentIndex(0);
  }, [
    onModalClose,
    currentStep,
    currentIndex,
    debouncedUpdateStep,
    debouncedUpdateIndex,
  ]);

  const selectRun = useCallback(
    (run: RunWithHistoryAndMediaWandb) => {
      setSelectedRun(run);

      if (stepMeta) {
        const runStepData = stepMeta.runs.find(r => r.id === run.name);
        if (runStepData) {
          const clampedStep = Math.max(
            runStepData.min,
            Math.min(currentStep, runStepData.max)
          );
          setCurrentStep(clampedStep);

          const clampedIndex = Math.max(
            runStepData.minIndex,
            Math.min(currentIndex, runStepData.maxIndex)
          );
          setCurrentIndex(clampedIndex);
        }
      }
    },
    [stepMeta, currentStep, currentIndex]
  );

  const navigateToPrevious = useCallback(() => {
    if (selectedRun) {
      const currentIndex = runsWithMedia.findIndex(
        run => run.name === selectedRun.name
      );
      if (currentIndex > 0) {
        const previousRun = runsWithMedia[currentIndex - 1];
        selectRun(previousRun);
      }
    }
  }, [selectedRun, runsWithMedia, selectRun]);

  const navigateToNext = useCallback(() => {
    if (selectedRun) {
      const currentIndex = runsWithMedia.findIndex(
        run => run.name === selectedRun.name
      );
      if (currentIndex >= 0 && currentIndex < runsWithMedia.length - 1) {
        const nextRun = runsWithMedia[currentIndex + 1];
        selectRun(nextRun);
      }
    }
  }, [selectedRun, runsWithMedia, selectRun]);

  const updateStep = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const updateIndex = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const currentRunIndex = useMemo(() => {
    if (!selectedRun || !runsWithMedia.length) {
      return 0;
    }
    return runsWithMedia.findIndex(run => run.name === selectedRun?.name);
  }, [selectedRun, runsWithMedia]);

  // Compute navigation state
  const hasNext =
    currentRunIndex >= 0 && currentRunIndex < runsWithMedia.length - 1;
  const hasPrevious = currentRunIndex > 0;

  // Compute step slider data (only recalculates when run or step meta changes)
  const stepSliderData = useMemo(() => {
    if (!selectedRun || !stepMeta) {
      return null;
    }

    const selectedRunStepData = stepMeta.runs.find(
      runData => runData.id === selectedRun.name
    );

    if (!selectedRunStepData) {
      return null;
    }

    return {
      min: selectedRunStepData.minSliderStep,
      max: selectedRunStepData.maxSliderStep,
      steps: selectedRunStepData.sliderSteps,
      key: stepMeta.sliderKey,
      label: keyDisplayName({section: 'summary', name: stepMeta.sliderKey}),
      keyIsStep: stepMeta.sliderKey === '_step',
      allowAnyValue: !sliderConfig.snapToExistingStep,
      sliderStepMap: selectedRunStepData.sliderStepMap,
      stepStrideLength: sliderConfig.stepStrideLength,
    };
  }, [selectedRun, stepMeta, sliderConfig]);

  const currentTileMediaSpec = useMemo((): TileMediaSpec | null => {
    if (!modalData || !selectedRun) {
      return null;
    }

    const mappedStep = stepSliderData?.sliderStepMap?.get(currentStep);

    return {
      ...modalData.tileMediaSpec,
      mediaStep: mappedStep ?? currentStep,
      mediaIndex: currentIndex,
      run: selectedRun,
      runSignature: {
        entityName: selectedRun.entityName,
        projectName: selectedRun.projectName,
        runName: selectedRun.name,
      },
    };
  }, [modalData, selectedRun, currentStep, currentIndex, stepSliderData]);

  const indexSliderData = useMemo(() => {
    if (!selectedRun || !stepMeta) {
      return null;
    }

    const selectedRunStepData = stepMeta.runs.find(
      runData => runData.id === selectedRun.name
    );

    if (!selectedRunStepData) {
      return null;
    }

    return {
      min: selectedRunStepData.minIndex,
      max: selectedRunStepData.maxIndex,
      indices: selectedRunStepData.indices,
    };
  }, [selectedRun, stepMeta]);

  const sliderData = useMemo(() => {
    if (!stepSliderData || !indexSliderData) {
      return {stepSlider: null, indexSlider: null};
    }

    const mappedCurrentStep =
      Array.from(stepSliderData.sliderStepMap?.entries() ?? []).find(
        ([_, val]) => val === currentStep
      )?.[0] ?? currentStep;
    const stepSlider = {
      ...stepSliderData,
      current: mappedCurrentStep,
    };

    const indexSlider = {
      ...indexSliderData,
      current: currentIndex,
    };

    return {stepSlider, indexSlider};
  }, [stepSliderData, indexSliderData, currentStep, currentIndex]);

  const value: ModalContextValue = useMemo(
    () => ({
      // State management
      isOpen,
      modalData,

      // Actions
      openModal,
      updateModalData: setModalData,
      closeModal,

      // Navigation actions
      selectRun,
      navigateToPrevious,
      navigateToNext,

      // Step/Index actions
      updateStep,
      updateIndex,
      debouncedUpdateStep,
      debouncedUpdateIndex,

      // Processed data for components
      currentTileMediaSpec,
      navigation: {
        hasNext,
        hasPrevious,
        currentRun: selectedRun,
        availableRuns: runsWithMedia,
      },
      sliderData,
      sliderConfig,
    }),
    [
      isOpen,
      modalData,
      openModal,
      closeModal,
      selectRun,
      navigateToPrevious,
      navigateToNext,
      updateStep,
      updateIndex,
      debouncedUpdateStep,
      debouncedUpdateIndex,
      currentTileMediaSpec,
      hasNext,
      hasPrevious,
      selectedRun,
      runsWithMedia,
      sliderData,
      sliderConfig,
    ]
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
};
